import React, { useState, useEffect, useRef } from 'react';
import { motion, useMotionValue, animate } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { api, useLanguage } from '../context/AppContext';
import {
  Shield, Flame, Target, Timer, Dumbbell, TrendingUp,
  Droplet, Moon, Brain, Sparkles,
} from 'lucide-react';

/**
 * 🛡️ Discipline Score widget — Yuri spec 2026-02 Iter. 319 ELITE EDITION.
 *
 * Now aggregates 7 pillars (max 150 pts, baseline 100):
 *   protein 30 · calories 25 · fasting 25 · activity 20 ·
 *   hydration 20 · sleep 20 · mindset 10
 *
 * Animation contract:
 *   1. Progressive ring fill on first load: 0 → score in 1.2s (easeOut).
 *   2. Live pulse + "+N pts" floating badge each time a pillar flips to hit.
 *   3. Above 100 → ring color transitions from gold-electric to blue/white
 *      sparkling; "EXTRA MILE" label appears.
 */

const PILLAR_CONFIG = {
  protein:   { icon: Target,   label_fr: 'Protéines',   label_en: 'Protein',     order: 1 },
  calories:  { icon: Flame,    label_fr: 'Calories',    label_en: 'Calories',    order: 2 },
  fasting:   { icon: Timer,    label_fr: 'Jeûne',       label_en: 'Fasting',     order: 3 },
  activity:  { icon: Dumbbell, label_fr: 'Activité',    label_en: 'Activity',    order: 4 },
  hydration: { icon: Droplet,  label_fr: 'Hydratation', label_en: 'Hydration',   order: 5 },
  sleep:     { icon: Moon,     label_fr: 'Sommeil',     label_en: 'Sleep',       order: 6 },
  mindset:   { icon: Brain,    label_fr: 'Mindset',     label_en: 'Mindset',     order: 7 },
};

const GOLD_ELECTRIC_GLOW = '0 0 18px rgba(250,204,21,0.50), 0 0 6px rgba(245,158,11,0.40), inset 0 0 0 1px rgba(250,204,21,0.18)';
const EXTRA_MILE_GLOW    = '0 0 28px rgba(125,211,252,0.70), 0 0 10px rgba(255,255,255,0.55), inset 0 0 0 1px rgba(186,230,253,0.35)';

// One-shot keyframes injection (idempotent).
let _keysInjected = false;
function injectKeyframes() {
  if (typeof document === 'undefined' || _keysInjected) return;
  _keysInjected = true;
  const style = document.createElement('style');
  style.setAttribute('data-discipline-keys', '1');
  style.textContent = `
    @keyframes mv-flame-pulse {
      0%, 100% { transform: scale(1); filter: drop-shadow(0 0 4px rgba(251,146,60,0.6)); }
      50%      { transform: scale(1.08); filter: drop-shadow(0 0 10px rgba(251,146,60,0.95)); }
    }
    @keyframes mv-extra-shimmer {
      0%   { background-position: 0% 50%; }
      100% { background-position: 200% 50%; }
    }
    @keyframes mv-pillar-flash {
      0%   { box-shadow: 0 0 4px rgba(250,204,21,0.0); transform: scale(1); }
      35%  { box-shadow: 0 0 22px rgba(250,204,21,0.95), 0 0 6px rgba(245,158,11,0.85); transform: scale(1.05); }
      100% { box-shadow: 0 0 14px rgba(251,146,60,0.55), 0 0 4px rgba(245,158,11,0.45); transform: scale(1); }
    }
    @keyframes mv-pop {
      0%   { transform: translateY(0) scale(0.8); opacity: 0; }
      20%  { transform: translateY(-4px) scale(1.05); opacity: 1; }
      100% { transform: translateY(-22px) scale(1); opacity: 0; }
    }
  `;
  document.head.appendChild(style);
}

// Animated counter — drives a smooth 0 → score transition.
function AnimatedScore({ score, color }) {
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const controls = animate(mv, score, {
      duration: 1.2,
      ease: [0.16, 1, 0.3, 1], // easeOutExpo-ish
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [score, mv]);
  return <span className={`font-heading text-2xl font-black tabular-nums ${color}`}>{display}</span>;
}

const DisciplineScore = () => {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [weekly, setWeekly] = useState(null); // {current, previous, delta, delta_pct, is_new_record}
  const [pillarFlash, setPillarFlash] = useState(null); // {key, points} for the +N flash
  const lastPillarsRef = useRef({});
  const pollTimerRef = useRef(null);

  useEffect(() => {
    injectKeyframes();
  }, []);

  // Initial fetch + 30s poll so live validations (e.g., "log a meal" then return
  // to dashboard) trigger the +N pulse without a full refresh.
  useEffect(() => {
    let alive = true;
    const fetchOnce = () => api.get('/discipline/today')
      .then((res) => { if (alive) setData(res.data); })
      .catch(() => {});
    const fetchProgress = () => api.get('/points/weekly-progress')
      .then((res) => { if (alive) setWeekly(res.data); })
      .catch(() => {});
    fetchOnce();
    fetchProgress();
    pollTimerRef.current = setInterval(() => {
      fetchOnce();
      fetchProgress();
    }, 30_000);
    return () => {
      alive = false;
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, []);

  // Detect a pillar flipping from miss → hit between two snapshots.
  useEffect(() => {
    if (!data?.pillars) return;
    const prev = lastPillarsRef.current;
    for (const [key, p] of Object.entries(data.pillars)) {
      const prevHit = !!prev[key]?.hit;
      if (!prevHit && p.hit) {
        setPillarFlash({ key, points: p.score, ts: Date.now() });
        // Auto-clear so the animation runs only once
        setTimeout(() => setPillarFlash((cur) => (cur?.ts === Date.now() ? null : cur)), 1300);
        break;
      }
    }
    lastPillarsRef.current = data.pillars;
  }, [data]);

  if (!data) return null;

  const {
    score, max = 150, baseline = 100, extra_mile, grade_emoji, grade_label, pillars,
  } = data;

  // Ring math — visualize against the 150 cap so the gold zone fills first
  // (until 100, the ring fills the "main loop"), then the "extra" zone tops it
  // off with the blue/white shimmer above 100.
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const ringPct = Math.min(1, score / max);
  const progress = ringPct * circumference;

  // Color & glow palette — flips at the baseline (100).
  const isExtra = !!extra_mile || score > baseline;
  const ringColor = isExtra
    ? 'stroke-sky-200'
    : score >= baseline * 0.85
      ? 'stroke-amber-300'
      : score >= baseline * 0.6
        ? 'stroke-amber-400'
        : score >= baseline * 0.3
          ? 'stroke-orange-400'
          : 'stroke-red-400';
  const scoreColor = isExtra
    ? 'text-sky-100'
    : score >= baseline * 0.85
      ? 'text-amber-300'
      : score >= baseline * 0.6
        ? 'text-amber-400'
        : score >= baseline * 0.3
          ? 'text-orange-400'
          : 'text-red-400';
  const ringGlow = isExtra
    ? `drop-shadow(0 0 10px rgba(186,230,253,0.95)) drop-shadow(0 0 4px rgba(255,255,255,0.65))`
    : score >= baseline * 0.85
      ? `drop-shadow(0 0 8px rgba(251,146,60,0.55))`
      : 'none';

  const cardGlow = isExtra ? EXTRA_MILE_GLOW : GOLD_ELECTRIC_GLOW;

  // Iter. 404.d — Hide the Fasting tile entirely when the user has no
  // fasting protocol (`has_protocol: false` from backend). The backend still
  // awards the implicit 23/23 points so the discipline math stays at 100,
  // but showing a "✓ Jeûne 23/23" tile to someone who doesn't fast is
  // dishonest UX and was confusing users (cf. user feedback May 31).
  // Perfect day detection — pillars relevant to *this* user only.
  const pillarEntries = Object.entries(pillars || {})
    .filter(([key, pillar]) => {
      // Hide fasting tile completely if the user opted out (`fasting_protocol = "none"`).
      if (key === 'fasting' && pillar && pillar.has_protocol === false) return false;
      return true;
    })
    .sort(([a], [b]) => (PILLAR_CONFIG[a]?.order || 99) - (PILLAR_CONFIG[b]?.order || 99));
  const hitsCount = pillarEntries.filter(([_k, p]) => p.hit).length;
  const totalPillars = pillarEntries.length;
  const perfectDay = totalPillars > 0 && hitsCount === totalPillars;

  return (
    <div
      className={`relative rounded-xl border p-4 overflow-visible transition-colors duration-500 ${
        isExtra
          ? 'border-sky-300/55 bg-gradient-to-br from-sky-950/35 via-zinc-950/85 to-indigo-950/30'
          : perfectDay
            ? 'border-amber-400/55 bg-gradient-to-br from-amber-500/15 via-orange-500/12 to-amber-500/8'
            : 'border-yellow-400/40 bg-gradient-to-br from-yellow-950/30 via-card to-amber-950/20'
      }`}
      style={{
        boxShadow: cardGlow,
        ...(isExtra
          ? {
              backgroundSize: '200% 100%',
              animation: 'mv-extra-shimmer 8s linear infinite',
            }
          : {}),
      }}
      data-testid="discipline-score-widget"
      data-extra-mile={isExtra ? '1' : '0'}
    >
      {/* Top-right badge — perfect day OR extra mile (GLOW aesthetic) */}
      {(perfectDay || isExtra) && (() => {
        const rgb = isExtra ? '125,211,252' : '251,146,60';   // sky-300 | orange-400
        const textCol = isExtra ? '#e0f2fe' : '#fef3c7';
        return (
          <div
            className="absolute -top-2 -right-2 flex items-center gap-1 px-2.5 py-1 rounded-full z-10"
            data-testid={isExtra ? 'discipline-extra-mile-badge' : 'discipline-perfect-badge'}
            style={{
              background: `linear-gradient(135deg, rgba(0,0,0,0.75) 0%, rgba(15,15,15,0.7) 45%, rgba(${rgb},0.28) 100%)`,
              border: `1px solid rgba(${rgb},0.6)`,
              boxShadow: `0 0 16px rgba(${rgb},0.55), 0 0 4px rgba(${rgb},0.35), inset 0 1px 0 rgba(255,255,255,0.08)`,
              backdropFilter: 'blur(6px)',
              animation: 'mv-flame-pulse 1.4s ease-in-out infinite',
            }}
          >
            <span
              className="text-xs leading-none"
              style={{ filter: `drop-shadow(0 0 6px rgba(${rgb},0.95))` }}
            >
              {isExtra ? '💎' : '🔥'}
            </span>
            <span
              className="text-[9px] font-black uppercase tracking-widest"
              style={{ color: textCol, textShadow: `0 0 6px rgba(${rgb},0.7)` }}
            >
              {isExtra ? (fr ? 'Extra Mile' : 'Extra Mile') : `${hitsCount}/${totalPillars}`}
            </span>
          </div>
        );
      })()}

      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Shield className={`w-4 h-4 ${isExtra ? 'text-sky-200' : perfectDay ? 'text-amber-400' : 'text-primary'}`} />
        <span className={`font-heading text-xs uppercase tracking-widest ${isExtra ? 'text-sky-200' : perfectDay ? 'text-amber-400' : 'text-primary'}`}>
          {fr ? 'Score de Discipline' : 'Discipline Score'}
        </span>
        {isExtra && (
          <span className="ml-auto text-[9px] font-mono uppercase tracking-[0.28em] text-sky-200/85"
            style={{ textShadow: '0 0 6px rgba(186,230,253,0.6)' }}>
            ★ {fr ? 'Au-delà du 100%' : 'Beyond 100%'}
          </span>
        )}
      </div>

      {/* Score Ring + Pillars */}
      <div className="flex items-center gap-4">
        <div className="relative w-24 h-24 shrink-0">
          <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100" style={{ filter: ringGlow }}>
            {/* Track */}
            <circle cx="50" cy="50" r={radius} fill="none" stroke="currentColor" strokeWidth="6" className="text-secondary" />
            {/* Baseline tick at 100/150 (= 66.66% of circumference) — discreet but present */}
            <circle
              cx="50" cy="50" r={radius} fill="none" strokeWidth="1" strokeDasharray="3 6"
              className={isExtra ? 'stroke-sky-300/40' : 'stroke-amber-400/25'}
            />
            {/* Progress */}
            <motion.circle
              cx="50" cy="50" r={radius} fill="none" strokeWidth="6" strokeLinecap="round"
              className={ringColor}
              strokeDasharray={circumference}
              initial={{ strokeDashoffset: circumference }}
              animate={{ strokeDashoffset: circumference - progress }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <AnimatedScore score={score} color={scoreColor} />
            <span className={`text-[9px] ${isExtra ? 'text-sky-200/75' : 'text-muted-foreground'}`}>
              /{baseline}{isExtra ? ' ✦' : ''}
            </span>
          </div>
        </div>

        {/* Pillars grid — 7 entries laid out in 2 cols (last one spans full width on row 4) */}
        <div className="flex-1 grid grid-cols-2 gap-1.5 relative">
          {pillarEntries.map(([key, pillar]) => {
            const cfg = PILLAR_CONFIG[key];
            if (!cfg) return null;
            const Icon = cfg.icon;
            const hit = !!pillar.hit;
            const flashing = pillarFlash?.key === key;
            return (
              <div
                key={key}
                className={`relative flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-all ${
                  hit
                    ? isExtra
                      ? 'bg-gradient-to-br from-sky-400/25 to-indigo-400/15 ring-1 ring-sky-300/55'
                      : 'bg-gradient-to-br from-orange-500/20 to-amber-500/10 ring-1 ring-amber-400/50'
                    : 'bg-secondary/40 ring-1 ring-transparent'
                }`}
                style={{
                  ...(hit && !isExtra ? { boxShadow: '0 0 14px rgba(251,146,60,0.55), 0 0 4px rgba(245,158,11,0.45)' } : {}),
                  ...(hit && isExtra ? { boxShadow: '0 0 14px rgba(125,211,252,0.65), 0 0 4px rgba(186,230,253,0.55)' } : {}),
                  ...(flashing ? { animation: 'mv-pillar-flash 1.1s ease-out 1' } : {}),
                }}
                data-testid={`discipline-pillar-${key}`}
              >
                <Icon className={`w-3.5 h-3.5 ${hit ? (isExtra ? 'text-sky-100' : 'text-amber-300') : 'text-muted-foreground/40'}`} />
                <div className="min-w-0 flex-1">
                  <p className={`text-[10px] font-bold truncate ${hit ? (isExtra ? 'text-sky-100' : 'text-amber-300') : 'text-muted-foreground/50'}`}>
                    {fr ? cfg.label_fr : cfg.label_en}
                  </p>
                  <p className={`text-[9px] tabular-nums ${hit ? (isExtra ? 'text-sky-200/85' : 'text-amber-400/80') : 'text-muted-foreground'}`}>
                    {/* Iter. 404.d — Honest fasting copy: if the user HAS a
                        protocol but skipped today, show "Pas pratiqué"
                        instead of a misleading "0/23". Other pillars keep
                        the numeric format. */}
                    {key === 'fasting' && pillar.has_protocol && !hit
                      ? (fr ? 'Pas pratiqué' : 'Not done')
                      : `${pillar.score}/${pillar.max}`}
                  </p>
                </div>
                {hit && (
                  <span className={`text-[8px] font-black absolute top-0.5 right-1 ${isExtra ? 'text-sky-200' : 'text-amber-300'}`} aria-hidden="true">✓</span>
                )}
                {flashing && (
                  <span
                    className={`pointer-events-none absolute -top-1 right-1 text-[10px] font-black ${isExtra ? 'text-sky-100' : 'text-amber-300'}`}
                    style={{
                      animation: 'mv-pop 1.2s ease-out forwards',
                      textShadow: isExtra
                        ? '0 0 8px rgba(186,230,253,0.95)'
                        : '0 0 8px rgba(251,146,60,0.85)',
                    }}
                  >
                    +{pillarFlash.points}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Weekly progress badge — GLOW aesthetic (Iter. 411.y) */}
      {weekly && weekly.current > 0 && (() => {
        // 3 states: new-record (amber intensified), positive delta (orange),
        // negative delta (muted violet as a "warning" cue).
        const rgb = weekly.is_new_record
          ? '245,158,11'   // amber-500
          : weekly.delta >= 0
            ? '251,146,60' // orange-400
            : '168,85,247'; // purple-500
        return (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); navigate('/points-history'); }}
            data-testid="discipline-weekly-progress-badge"
            className="mt-3 w-full flex items-center justify-between gap-2 rounded-lg px-3 py-2 transition-all text-left active:scale-[0.98]"
            style={{
              background: `linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(${rgb},0.20) 100%)`,
              border: `1px solid rgba(${rgb},${weekly.is_new_record ? 0.6 : 0.45})`,
              boxShadow: weekly.is_new_record
                ? `0 0 20px rgba(${rgb},0.55), 0 0 6px rgba(${rgb},0.4), inset 0 1px 0 rgba(255,255,255,0.06)`
                : `0 0 14px rgba(${rgb},0.38), inset 0 1px 0 rgba(255,255,255,0.05)`,
              backdropFilter: 'blur(6px)',
            }}
          >
            <div className="flex items-center gap-2 min-w-0">
              <Sparkles
                className="w-3.5 h-3.5 shrink-0"
                style={{
                  color: `rgba(${rgb},1)`,
                  filter: `drop-shadow(0 0 5px rgba(${rgb},0.9))`,
                  ...(weekly.is_new_record ? { animation: 'mv-flame-pulse 1.4s ease-in-out infinite' } : {}),
                }}
              />
              <div className="min-w-0">
                <p className="text-xs font-bold truncate" style={{ color: '#f8fafc' }}>
                  <span
                    className="font-black"
                    style={{ color: `rgba(${rgb},1)`, textShadow: `0 0 6px rgba(${rgb},0.65)` }}
                  >
                    +{weekly.current}
                  </span>{' '}
                  {fr ? 'pts cette semaine' : 'pts this week'}
                  {weekly.is_new_record && (
                    <span
                      className="ml-1.5 text-[10px] font-black uppercase tracking-widest"
                      style={{ color: `rgba(${rgb},1)`, textShadow: `0 0 6px rgba(${rgb},0.7)` }}
                    >
                      🔥 {fr ? 'nouveau record' : 'new record'}
                    </span>
                  )}
                </p>
                <p className="text-[10px] truncate" style={{ color: `rgba(${rgb},0.7)` }}>
                  {weekly.previous > 0
                    ? (fr
                        ? `vs ${weekly.previous} pts la semaine dernière · ${weekly.delta_pct >= 0 ? '+' : ''}${weekly.delta_pct}%`
                        : `vs ${weekly.previous} pts last week · ${weekly.delta_pct >= 0 ? '+' : ''}${weekly.delta_pct}%`)
                    : (fr ? 'Premiers points de la semaine 💪' : 'First points of the week 💪')}
                </p>
              </div>
            </div>
            <TrendingUp
              className={`w-4 h-4 shrink-0 ${weekly.delta < 0 ? 'rotate-180' : ''}`}
              style={{
                color: `rgba(${rgb},1)`,
                filter: `drop-shadow(0 0 5px rgba(${rgb},0.85))`,
              }}
            />
          </button>
        );
      })()}

      {/* Grade label + CTA "Voir mes points" — GLOW aesthetic (Iter. 411.y) */}
      <div className="mt-3 flex items-center justify-between">
        <span className={`text-xs font-bold ${scoreColor}`}>
          {grade_emoji} {grade_label}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); navigate('/points-history'); }}
            data-testid="discipline-see-points-cta"
            className="text-[10px] font-heading font-black uppercase tracking-widest px-3 py-1 rounded-full transition-all active:scale-95"
            style={{
              background: isExtra
                ? 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(15,15,15,0.65) 45%, rgba(125,211,252,0.22) 100%)'
                : 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(15,15,15,0.65) 45%, rgba(251,146,60,0.22) 100%)',
              border: isExtra
                ? '1px solid rgba(125,211,252,0.55)'
                : '1px solid rgba(251,146,60,0.55)',
              boxShadow: isExtra
                ? '0 0 12px rgba(125,211,252,0.45), inset 0 1px 0 rgba(255,255,255,0.06)'
                : '0 0 12px rgba(251,146,60,0.45), inset 0 1px 0 rgba(255,255,255,0.06)',
              backdropFilter: 'blur(6px)',
              color: isExtra ? '#e0f2fe' : '#fed7aa',
              textShadow: isExtra
                ? '0 0 5px rgba(125,211,252,0.6)'
                : '0 0 5px rgba(251,146,60,0.6)',
            }}
          >
            {fr ? 'Voir mes points →' : 'See my points →'}
          </button>
          <span
            className="text-[10px] font-black uppercase tracking-widest flex items-center gap-1 px-2.5 py-1 rounded-full"
            style={{
              background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(15,15,15,0.65) 45%, rgba(16,185,129,0.20) 100%)',
              border: '1px solid rgba(16,185,129,0.5)',
              boxShadow: '0 0 10px rgba(16,185,129,0.4)',
              backdropFilter: 'blur(6px)',
              color: '#a7f3d0',
              textShadow: '0 0 5px rgba(16,185,129,0.55)',
            }}
          >
            <TrendingUp className="w-3 h-3" style={{ filter: 'drop-shadow(0 0 4px rgba(16,185,129,0.85))' }} />
            {fr ? '7 jours' : '7 days'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default DisciplineScore;
